<template>
  <div>
    <div class="maker-select-methods button-group">
      <gl-button
        class="button-group__button"
        :primary="makerSelectMethod === 'keyword'"
        @click="toggleMakerSelect('keyword')"
        >あいまい検索</gl-button
      >
      <gl-button
        class="button-group__button"
        :primary="makerSelectMethod === 'code'"
        @click="toggleMakerSelect('code')"
        >コード入力</gl-button
      >
    </div>
    <div class="makers-container" :class="{'no-maker': makers.length === 0 }">
      <maker-list
        :makers="makers"
        @update:makers="$emit('update:makers', $event)"
        deletable
      />
    </div>
    <div class="actions button-group">
      <div class="left"></div>
      <span
        class="maker-count"
        :class="{ 'exceeds-limit': makers.length > maxMakers }"
        >{{ makerCount }} / {{ maxMakers }} メーカー</span
      >
      <gl-button class="button-group__button" raised @click="removeAll"
        >全削除</gl-button
      >
    </div>

    <!-- メーカー検索 -->
    <md-dialog
      class="maker-select-dialog"
      :md-active="!!makerSelectComponent"
      @md-clicked-outside="makerSelectMethod = undefined"
    >
      <md-dialog-content>
        <component
          :is="makerSelectComponent"
          @selected="addMakers"
        />
        <gl-button
          class="button-dialog-close"
          icon-button
          @click="makerSelectMethod = undefined"
          ><md-icon>cancel</md-icon></gl-button
        >
      </md-dialog-content>
    </md-dialog>
  </div>
</template>

<script>
import MakerList from '@/components/MakerList.vue'
import MakerSelectByKeyword from '@/components/MakerSelectByKeyword'
import MakerSelectByCode from '@/components/MakerSelectByCode'

const MAX_MAKERS = process.env.VUE_APP_MAX_MAKERS
  ? Number(process.env.VUE_APP_MAX_MAKERS)
  : 50

/**
 * メーカー選択コンポーネント
 */
export default {
  components: {
    MakerList,
    MakerSelectByKeyword,
    MakerSelectByCode,
  },
  props: {
    /**
     * 分析対象メーカー一覧
     */
    makers: {
      type: Array,
      default: () => [],
    },
    errors: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      /**
       * 選択可能な最大メーカー数
       */
      maxMakers: MAX_MAKERS,
      /**
       * メーカー選択方式
       *
       * 曖昧検索やメーカーコードでの検索などを指定します。通常は検索用コンポーネントを
       * 子コンポーネントとして、子は`selected`イベントを発行することでこのコンポーネント
       * の選択メーカーへの追加を行ってもらいます。なので、検索用コンポーネントは`selected`
       * イベントでメーカーリストを送りさえすれば、このコンポーネントのメーカー検索用コンポーネント
       * として成り立ちます。
       */
      makerSelectMethod: undefined,
    }
  },
  watch: {
    makers: {
      immediate: true,
      handler(makers) {
        const errors = []
        if (makers.length > this.maxMakers) {
          errors.push(`選択できるメーカー数は${this.maxMakers}メーカーまでです`)
        }
        this.$emit('update:errors', errors)
      },
    },
  },
  computed: {
    /**
     * メーカー選択コンポーネント
     *
     * メーカー選択方式に対応するメーカー選択コンポーネントを取得します。
     * 画面描画のいらないコンポーネント（e.g. ファイル入力）はここで対応する
     * 必要はありません。
     */
    makerSelectComponent() {
      switch (this.makerSelectMethod) {
        case 'keyword':
          return MakerSelectByKeyword
        case 'code':
          return MakerSelectByCode
      }
      return undefined
    },
    /**
     * 選択されているメーカー数
     */
    makerCount() {
      return this.makers.length
    },
  },
  methods: {
    /**
     * メーカー選択コンポーネント切替
     */
    toggleMakerSelect(method) {
      if (this.makerSelectMethod === method) {
        this.makerSelectMethod = undefined
      } else {
        this.makerSelectMethod = method
      }
    },
    /**
     * メーカー一覧クリア
     */
    removeAll() {
      this.$emit('update:makers', [])
    },
    /**
     * メーカーの追加
     */
    addMakers(makers) {
      const newMakerCodes = makers.map(i =>
        [i.compnayCode, i.makerCode].join(','),
      )

      // TODO パフォーマンスで課題が出たら修正
      this.$emit('update:makers', [
        ...this.makers.filter(
          i => !newMakerCodes.includes([i.compnayCode, i.makerCode].join(',')),
        ),
        ...makers,
      ])

      this.makerSelectMethod = undefined
    },
  },
}
</script>

<style lang="scss" scoped>
.makers-container {
  position: relative;
  height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.no-maker {
  background: rgba(0, 0, 0, 0.12);

  &::after {
    font-family: 'Material Icons';
    content: 'NO MAKERS';
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    font-size: 58px;
    color: rgba(0, 0, 0, 0.12);
  }
}

.maker-select-dialog {
  min-width: 700px;
}

.button-dialog-close {
  position: fixed;
  top: 6px;
  right: 0;
}

.maker-select-methods,
.maker-select,
.makers-container {
  margin-top: var(--spacing-medium);
}

.maker-select {
  padding-left: calc(var(--spacing-large) * 2);
}

.actions {
  width: 100%;
  text-align: right;
  display: flex;
  align-items: center;

  .left {
    flex: 1;
  }
}

.maker-count {
  margin-right: var(--spacing-medium);
}

.exceeds-limit {
  color: var(--color-warn);
}
</style>
